import { createAsyncThunk, createSlice, isFulfilled, PayloadAction } from "@reduxjs/toolkit";
import { CreateTableViewRequest, CreateTableViewResponse, DeleteTableViewResponse, GetTableViewByIdResponse, NewNameTableViewRequest, RenameTableViewResponse, SetDefaultTableViewRequest, SetDefaultTableViewResponse, SettingsSliceModel, TableView, TableViewBasicInfo, TableViewName, TableViewSettings, ToggleFavoriteRequest, UpdateTableViewRequest, UpdateTableViewResponse, UserFavoritesResponse } from "./settings-interfaces";
import { ErrorResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { Guid } from "guid-typescript";
import { TableType } from "constants/enums";
import { setDefaultTableView } from "shared/utils/common";
import { API_BASE_URL as baseUri } from "config/env";

export const CreateTableViews = createAsyncThunk<TableViewSettings, CreateTableViewRequest, { rejectValue: RejectedValue }>(
    'Slice/Settings/CreateTableView',
    async (tableView: CreateTableViewRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/settings/createtableview', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(tableView)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TableViewSettings;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
)

export const SetDefaultTableView = createAsyncThunk<SetDefaultTableViewResponse, SetDefaultTableViewRequest, { rejectValue: RejectedValue }>(
    'Slice/Settings/SetDefaultTableView',
    async (tableViewId: SetDefaultTableViewRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/settings/settableviewasdefault', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(tableViewId)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as SetDefaultTableViewResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
)

export const UpdateTableView = createAsyncThunk<TableViewSettings, UpdateTableViewRequest, { rejectValue: RejectedValue }>(
    'Slice/Settings/UpdateTableView',
    async (tableView: UpdateTableViewRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/settings/updatetableview', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(tableView)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TableViewSettings;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
)

export const GetTableViewSettings = createAsyncThunk<TableViewSettings, TableType, { rejectValue: RejectedValue }>(
    'Slice/Settings/GetTableViewSettings',
    async (tableType: TableType, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/settings/gettableviewsettings?tableType=${tableType}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TableViewSettings;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
)

export const GetTableView = createAsyncThunk<TableView, Guid, { rejectValue: RejectedValue }>(
    'Slice/Settings/GetTableView',
    async (tableViewId: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/settings/gettableview?tableViewId=${tableViewId}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TableView;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
)

export const DeleteTableView = createAsyncThunk<TableViewSettings, Guid, { rejectValue: RejectedValue }>(
    'Slice/Settings/DeleteTableView',
    async (tableViewId: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/settings/deletetableview?tableViewId=${tableViewId}`, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TableViewSettings;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
)

export const RenameTableView = createAsyncThunk<RenameTableViewResponse, NewNameTableViewRequest, { rejectValue: RejectedValue }>(
    'Slice/Settings/RenameTableView',
    async (newNameTableViewRequest: NewNameTableViewRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/settings/renametableview`, {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(newNameTableViewRequest)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as RenameTableViewResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
)

export const GetFavorites = createAsyncThunk<UserFavoritesResponse, void, { rejectValue: RejectedValue }>(
    'Slice/Settings/GetFavorites',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/settings/getfavorites', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            return await response.json() as UserFavoritesResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
)

export const ToggleFavorite = createAsyncThunk<UserFavoritesResponse, ToggleFavoriteRequest, { rejectValue: RejectedValue }>(
    'Slice/Settings/ToggleFavorite',
    async (request: ToggleFavoriteRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/settings/togglefavorite', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            return await response.json() as UserFavoritesResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
)

const initialValue: SettingsSliceModel = {
    isLoading: false,
    tableViewSettings: null,
    favorites: [],
    statusCode: "",
    message: "",
    showNotification: false,
    reloadData: false,
}

export const SettingsSlice = createSlice({
    name: 'SettingsSlice',
    initialState: initialValue,
    reducers: {
        resetSettingsSlice: (state) => {
            state = initialValue
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
        },
        resetFavorites: (state) => {
            state.favorites = [];
        },
    },
    extraReducers(builder) {
        builder.addCase(GetTableViewSettings.fulfilled, (state, action: PayloadAction<TableViewSettings>) => {
            state.isLoading = false;
            state.message = '';
            state.tableViewSettings = action.payload;
        })
            .addCase(GetTableViewSettings.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetTableViewSettings.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(DeleteTableView.fulfilled, (state, action: PayloadAction<TableViewSettings>) => {
            state.isLoading = false;
            state.tableViewSettings = action.payload;
            state.message = 'The Table View is deleted';
            state.statusCode = "200";
            state.showNotification = true;
        })
            .addCase(DeleteTableView.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeleteTableView.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(CreateTableViews.fulfilled, (state, action: PayloadAction<TableViewSettings>) => {
            state.isLoading = false;
            state.message = '';
            state.tableViewSettings = action.payload;
        })
            .addCase(CreateTableViews.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateTableViews.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(UpdateTableView.fulfilled, (state, action: PayloadAction<TableViewSettings>) => {
            state.isLoading = false;
            state.message = '';
            state.tableViewSettings = action.payload;
        })
            .addCase(UpdateTableView.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateTableView.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(SetDefaultTableView.fulfilled, (state, action: PayloadAction<SetDefaultTableViewResponse>) => {
            state.isLoading = false;
            setDefaultTableView(state.tableViewSettings as TableViewSettings, action);

            state.message = 'The Table View is set as default';
            state.statusCode = "200";
            state.showNotification = true;
        })
            .addCase(SetDefaultTableView.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(SetDefaultTableView.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(GetTableView.fulfilled, (state, action: PayloadAction<TableView>) => {
            state.isLoading = false;
            state.message = '';
            state.tableViewSettings!.tableViewDTO = action.payload;
        })
            .addCase(GetTableView.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetTableView.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(GetFavorites.fulfilled, (state, action: PayloadAction<UserFavoritesResponse>) => {
            state.favorites = action.payload?.favorites ?? [];
        })
            .addCase(GetFavorites.rejected, (state, action) => {
                state.favorites = [];
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })

        builder.addCase(ToggleFavorite.fulfilled, (state, action: PayloadAction<UserFavoritesResponse>) => {
            state.favorites = action.payload?.favorites ?? [];
        })
            .addCase(ToggleFavorite.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
    }
});

export const { resetSettingsSlice, resetStatusCode, resetFavorites } = SettingsSlice.actions;
export default SettingsSlice.reducer;